import React, { useEffect, useState } from 'react';
import { Form, Input, Upload, Button, Typography, message, Row, Col, Card, Space } from 'antd';
import { UploadOutlined, EyeOutlined, EditOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import { uploadLabDocument } from '@app/api/labs.api';

const { Text } = Typography;

interface UploadedFile {
  lab_request_department_id: number;
  id: string;
  name: string;
  file: File | string;
  image_url?: string;
  departmentId: number;
}

interface Department {
  id: number;
  name: string;
}

interface LabDocumentChecklistProps {
  labRequestId: string;
  labRequestDepartmentId: Department | Department[];
  status?: string;
  editable: boolean;
  uploadedFiles?: UploadedFile[];
}

const LabDocumentChecklist: React.FC<LabDocumentChecklistProps> = ({
  labRequestId,
  labRequestDepartmentId,
  editable,
  uploadedFiles: initialUploadedFiles = [],
}) => {
  const [forms] = useState(() => new Map<number, any>());
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingMap, setEditingMap] = useState<Map<number, number | null>>(new Map());

  const departments = Array.isArray(labRequestDepartmentId) ? labRequestDepartmentId : [labRequestDepartmentId];

  useEffect(() => {
    if (initialUploadedFiles && initialUploadedFiles.length > 0) {
      const normalizedFiles = initialUploadedFiles.map((file) => ({
        ...file,
        departmentId: file.departmentId ?? file.lab_request_department_id,
      }));
      setUploadedFiles(normalizedFiles);
    }
  }, [initialUploadedFiles]);

  const normFile = (e: any) => (Array.isArray(e) ? e : e?.fileList);
  console.log('Initial uploaded files:', initialUploadedFiles);
  console.log('Uploaded files state:', uploadedFiles);

  const handleSubmit = async (dept: Department) => {
    const form = forms.get(dept.id);
    if (!form) return;

    try {
      const values = await form.validateFields();
      console.log('Form values:', values);

      const editingIndex = editingMap.get(dept.id);
      const isEditing = editingIndex !== null && editingIndex !== undefined;

      const payload: any = {
        id: isEditing ? uploadedFiles[editingIndex].id : uuidv4(),
        name: values.fileName,
      };

      if (values.file?.[0]) {
        payload.file = values.file[0].originFileObj;
      } else if (!isEditing) {
        message.error('Please select a file before submitting.');
        return;
      }

      setLoading(true);

      const response = await uploadLabDocument(labRequestId, String(dept.id), payload);

      console.log('API response:', response);

      const newFile: UploadedFile = {
        id: payload.id,
        name: values.fileName,
        file: payload.file || (typeof editingIndex === 'number' ? uploadedFiles[editingIndex]?.file : undefined),
        image_url: response?.data?.image_url,
        departmentId: dept.id,
        lab_request_department_id: 0,
      };

      if (isEditing) {
        const updated = [...uploadedFiles];
        updated[editingIndex] = newFile;
        setUploadedFiles(updated);
        setEditingMap((map) => new Map(map).set(dept.id, null));
        message.success('File updated!');
      } else {
        setUploadedFiles((prev) => [...prev, newFile]);
        message.success('File uploaded!');
      }

      form.resetFields();
    } catch (error) {
      console.error('Upload error:', error);
      message.error('An error occurred during upload.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (deptId: number, fileIndex: number) => {
    const form = forms.get(deptId);
    const file = uploadedFiles[fileIndex];
    if (form) {
      form.setFieldsValue({
        fileName: file.name,
        file: [],
      });
      setEditingMap((map) => new Map(map).set(deptId, fileIndex));
    }
  };

  const handleView = (file: File | string) => {
    const url = typeof file === 'string' ? file : URL.createObjectURL(file);
    window.open(url, '_blank');
  };

  return (
    <Row gutter={[16, 16]}>
      {departments.map((dept) => {
        const files = uploadedFiles.filter((f) => f.departmentId === dept.id);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [form] = Form.useForm();
        forms.set(dept.id, form);
        const editingIndex = editingMap.get(dept.id) ?? null;

        return (
          <Col key={dept.id} xs={24} md={12}>
            <div
              style={{
                border: '1px solid #d9d9d9',
                borderRadius: 8,
                padding: 16,
                marginBottom: 32,
                backgroundColor: '#fafafa',
              }}
            >
              <Text strong style={{ fontSize: 16 }}>
                {dept.name}
              </Text>

              {editable && (
                <Form form={form} layout="vertical" size="small" style={{ marginTop: 12 }}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="fileName"
                        label="File Name"
                        rules={[{ required: true, message: 'Please enter a file name' }]}
                      >
                        <Input placeholder="Enter file name" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="file"
                        label={editingIndex !== null ? 'Replace File (optional)' : 'Upload File'}
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                      >
                        <Upload beforeUpload={() => false} maxCount={1}>
                          <Button icon={<UploadOutlined />} size="small">
                            Choose File
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Button type="primary" size="small" onClick={() => handleSubmit(dept)} loading={loading}>
                    {editingIndex !== null ? 'Update File' : 'Submit'}
                  </Button>
                  {editingIndex !== null && (
                    <Button
                      size="small"
                      style={{ marginLeft: 8 }}
                      onClick={() => {
                        form.resetFields();
                        setEditingMap((map) => new Map(map).set(dept.id, null));
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </Form>
              )}

              {files.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  {files.map((file, index) => (
                    <Card size="small" key={file.id} style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong>{file.name}</Text>
                        <Space>
                          <div
                            onClick={() => handleView(file.image_url || file.file)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              padding: '2px 8px',
                              border: '1px solid #d9d9d9',
                              borderRadius: 4,
                              cursor: 'pointer',
                            }}
                          >
                            <EyeOutlined />
                            <span>View</span>
                          </div>

                          {editable && (
                            <div
                              onClick={() =>
                                handleEdit(
                                  dept.id,
                                  uploadedFiles.findIndex((f) => f.id === file.id),
                                )
                              }
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                padding: '2px 8px',
                                border: '1px solid #d9d9d9',
                                borderRadius: 4,
                                cursor: 'pointer',
                              }}
                            >
                              <EditOutlined />
                              <span>Edit</span>
                            </div>
                          )}
                        </Space>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </Col>
        );
      })}
    </Row>
  );
};

export default LabDocumentChecklist;
